import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Database, Trash2, Loader2, AlertTriangle, CheckCircle2, Package, Truck, ClipboardList, Boxes, Undo2, Warehouse, Layers, UserPlus, ShoppingCart } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

var MODULE_PURGE_OPTIONS = [
  { key: 'inventory', label: 'Inventory', description: 'Stock Ledger, Adjustments, Batches & reset product stock to 0', icon: Boxes, color: 'blue' },
  { key: 'stock_master', label: 'Stock Master', description: 'Clear Stock Master grid + wipe all batches (keeps catalogue products; re-adds via fresh GRN/Bulk Upload)', icon: Warehouse, color: 'indigo' },
  { key: 'master_catalogue', label: 'Master Catalogue', description: 'All products from catalogue', icon: Package, color: 'purple' },
  { key: 'grn', label: 'GRN', description: 'All Goods Receipt Notes & mismatches', icon: Truck, color: 'green' },
  { key: 'purchase_orders', label: 'Purchase Orders', description: 'All POs raised', icon: ClipboardList, color: 'orange' },
  { key: 'purchase_returns', label: 'Purchase Returns', description: 'All returns & supplier ledger', icon: Undo2, color: 'red' },
  { key: 'master_data', label: 'Master Data', description: 'All Brands, Sub Brands, Categories, Menus, Sub Menus & Companies (dropdown lists)', icon: Layers, color: 'red' },
  { key: 'onboarding', label: 'Onboarding', description: 'All Franchise / Retailer / Distributor / Dark Store / C&F records', icon: UserPlus, color: 'red' },
  { key: 'franchisee_orders', label: 'Franchisee Orders', description: 'All franchisee orders & related Pick Lists (Invoices and DCs are NOT touched)', icon: ShoppingCart, color: 'red' },
];

function DatabaseCleanup() {
  var loadingState = React.useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var resultState = React.useState(null);
  var result = resultState[0];
  var setResult = resultState[1];

  var confirmState = React.useState(false);
  var showConfirm = confirmState[0];
  var setShowConfirm = confirmState[1];

  var purgeConfirmState = React.useState('');
  var purgeConfirm = purgeConfirmState[0];
  var setPurgeConfirm = purgeConfirmState[1];

  var purgeLoadingState = React.useState('');
  var purgeLoading = purgeLoadingState[0];
  var setPurgeLoading = purgeLoadingState[1];

  var purgeResultState = React.useState(null);
  var purgeResult = purgeResultState[0];
  var setPurgeResult = purgeResultState[1];

  function runCleanup() {
    setLoading(true);
    setShowConfirm(false);
    var token = localStorage.getItem('token');
    axios.post(API + '/admin/cleanup-test-data', {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setResult(res.data);
      setLoading(false);
      var total = (res.data.suppliers_removed || 0) + (res.data.products_removed || 0) +
        (res.data.test_users_removed || 0) + (res.data.onboarding_removed || 0) +
        (res.data.test_employees_removed || 0) + (res.data.mock_eway_bills_removed || 0) +
        (res.data.test_invoices_removed || 0) + (res.data.orphan_stock_ledger_removed || 0);
      if (total > 0) toast.success(total + ' test records removed');
      else toast.info('No test data found');
    }).catch(function(err) {
      toast.error(err.response && err.response.data ? err.response.data.detail : 'Cleanup failed');
      setLoading(false);
    });
  }

  function runModulePurge(moduleKey) {
    setPurgeLoading(moduleKey);
    setPurgeConfirm('');
    var token = localStorage.getItem('token');
    axios.post(API + '/admin/purge-module/' + moduleKey, {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(res) {
      setPurgeResult(res.data);
      setPurgeLoading('');
      toast.success(res.data.message);
    }).catch(function(err) {
      toast.error(err.response && err.response.data ? err.response.data.detail : 'Purge failed');
      setPurgeLoading('');
    });
  }

  return React.createElement(Card, { 'data-testid': 'database-cleanup-card' },
    React.createElement(CardHeader, { className: 'pb-3' },
      React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
        React.createElement(Database, { className: 'w-5 h-5 text-red-600' }),
        'Database Management')),

    React.createElement(CardContent, { className: 'space-y-6' },

      // Module-wise Purge Section
      React.createElement('div', null,
        React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide mb-3' }, 'Delete Module Data'),
        React.createElement('p', { className: 'text-xs text-slate-500 mb-4' }, 'Permanently delete ALL records from a specific module. This cannot be undone.'),
        React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3' },
          MODULE_PURGE_OPTIONS.map(function(mod) {
            var Icon = mod.icon;
            var isConfirming = purgeConfirm === mod.key;
            var isPurging = purgeLoading === mod.key;
            var colorMap = { blue: 'border-blue-200 bg-blue-50', purple: 'border-purple-200 bg-purple-50', green: 'border-green-200 bg-green-50', orange: 'border-orange-200 bg-orange-50', red: 'border-red-200 bg-red-50', indigo: 'border-indigo-200 bg-indigo-50' };
            var textMap = { blue: 'text-blue-700', purple: 'text-purple-700', green: 'text-green-700', orange: 'text-orange-700', red: 'text-red-700', indigo: 'text-indigo-700' };

            return React.createElement('div', { key: mod.key, className: 'p-4 rounded-lg border ' + (colorMap[mod.color] || 'bg-gray-50') },
              React.createElement('div', { className: 'flex items-center gap-2 mb-2' },
                React.createElement(Icon, { className: 'w-4 h-4 ' + (textMap[mod.color] || '') }),
                React.createElement('span', { className: 'font-semibold text-sm ' + (textMap[mod.color] || '') }, mod.label)),
              React.createElement('p', { className: 'text-xs text-slate-500 mb-3' }, mod.description),
              !isConfirming && React.createElement(Button, {
                variant: 'outline', size: 'sm', onClick: function() { setPurgeConfirm(mod.key); setPurgeResult(null); },
                className: 'w-full gap-1 border-red-300 text-red-600 hover:bg-red-50', disabled: isPurging,
                'data-testid': 'purge-' + mod.key + '-btn'
              }, React.createElement(Trash2, { className: 'w-3.5 h-3.5' }), 'Delete All'),
              isConfirming && React.createElement('div', { className: 'space-y-2' },
                React.createElement('p', { className: 'text-xs font-semibold text-red-600' }, 'Delete ALL ' + mod.label + ' data?'),
                React.createElement('div', { className: 'flex gap-2' },
                  React.createElement(Button, { size: 'sm', onClick: function() { runModulePurge(mod.key); }, className: 'flex-1 bg-red-600 hover:bg-red-700 gap-1', disabled: isPurging, 'data-testid': 'confirm-purge-' + mod.key },
                    isPurging ? React.createElement(Loader2, { className: 'w-3.5 h-3.5 animate-spin' }) : React.createElement(Trash2, { className: 'w-3.5 h-3.5' }),
                    isPurging ? 'Deleting...' : 'Confirm'),
                  React.createElement(Button, { variant: 'outline', size: 'sm', onClick: function() { setPurgeConfirm(''); } }, 'Cancel'))));
          }))),

      // Purge result
      purgeResult && React.createElement('div', { className: 'p-4 bg-green-50 border border-green-200 rounded-lg' },
        React.createElement('div', { className: 'flex items-center gap-2 mb-2' },
          React.createElement(CheckCircle2, { className: 'w-5 h-5 text-green-600' }),
          React.createElement('span', { className: 'font-semibold text-green-800' }, purgeResult.message)),
        React.createElement('div', { className: 'flex flex-wrap gap-2 mt-2' },
          Object.entries(purgeResult.deleted || {}).map(function(entry) {
            return React.createElement(Badge, { key: entry[0], className: 'bg-red-100 text-red-700' }, entry[0] + ': -' + entry[1]);
          }),
          purgeResult.stock_reset && React.createElement(Badge, { className: 'bg-blue-100 text-blue-700' }, 'Product stock reset to 0'),
          purgeResult.stock_master_flag_reset && React.createElement(Badge, { className: 'bg-indigo-100 text-indigo-700' }, 'Stock Master flag cleared'))),

      // Divider
      React.createElement('div', { className: 'border-t pt-4' },
        React.createElement('h3', { className: 'font-semibold text-sm text-slate-700 uppercase tracking-wide mb-3' }, 'Clean Test Data'),
        React.createElement('p', { className: 'text-xs text-slate-500 mb-3' }, 'Remove only records with TEST_ prefix. Your real data stays safe.'),
        !showConfirm && React.createElement(Button, {
          onClick: function() { setShowConfirm(true); },
          variant: 'outline', className: 'gap-2 border-amber-300 text-amber-700 hover:bg-amber-50',
          disabled: loading, 'data-testid': 'cleanup-test-data-btn'
        }, React.createElement(Trash2, { className: 'w-4 h-4' }), 'Clean Test Data'),
        showConfirm && React.createElement('div', { className: 'flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg' },
          React.createElement('p', { className: 'text-sm font-semibold text-amber-700' }, 'Remove test-prefixed records?'),
          React.createElement(Button, { onClick: runCleanup, size: 'sm', className: 'bg-amber-600 hover:bg-amber-700 gap-1', disabled: loading, 'data-testid': 'confirm-cleanup-btn' },
            loading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Trash2, { className: 'w-4 h-4' }),
            loading ? 'Cleaning...' : 'Yes'),
          React.createElement(Button, { variant: 'outline', size: 'sm', onClick: function() { setShowConfirm(false); }, disabled: loading }, 'Cancel')))));
}

export default DatabaseCleanup;
